import { useCommentData } from '../../context/CommentContext'
import { useEngagementInteraction } from '../../../hooks/engagement/useEngagementInteraction'

type UseCommentInteractionProps = {
  onExpandNextComment?: (
    fromCommentId?: string,
    onlyRepliable?: boolean,
  ) => void
  onExpandPreviousComment?: () => void
  onManualExpansion?: (commentId: string | null) => void
  onHoverReset?: () => void
}

type UseCommentInteractionReturn = {
  handleCommentClick: (e: React.MouseEvent) => void
  handleExpandNextFromAction: () => void
  handleCollapse: () => void
  handleKeyDown: (event: React.KeyboardEvent) => void
}

/**
 * Custom hook to manage comment interaction logic
 * Handles click, keyboard navigation, expansion, and collapse
 *
 * Thin wrapper over the shared `useEngagementInteraction`: reads the comment id
 * from context and delegates. A comment click never no-ops on expand (it
 * re-fires `onManualExpansion`, which is idempotent), so the guard is left off.
 */
export const useCommentInteraction = ({
  onExpandNextComment,
  onExpandPreviousComment,
  onManualExpansion,
  onHoverReset,
}: UseCommentInteractionProps): UseCommentInteractionReturn => {
  const comment = useCommentData()

  const {
    handleClick,
    handleExpandNextFromAction,
    handleCollapse,
    handleKeyDown,
  } = useEngagementInteraction({
    id: comment.id,
    onManualExpansion,
    onExpandNext: onExpandNextComment,
    onExpandPrevious: onExpandPreviousComment,
    onHoverReset,
    reactionSelector: '[data-comment-reaction], [data-comment-actions]',
  })

  return {
    handleCommentClick: handleClick,
    handleExpandNextFromAction,
    handleCollapse,
    handleKeyDown,
  }
}
